import type { SavedViewDto } from '@performance-crm/shared';
import { Bookmark, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { useAuth } from '@/lib/auth-context';
import { useDeleteView, useSaveView, useSavedViews } from '@/lib/queries';

/** Selector + guardado de vistas (filtros y columnas) para listado y kanban. */
export function SavedViews({
  scope,
  currentFilters,
  currentColumns,
  onApply,
}: {
  scope: 'LEADS' | 'PIPELINE';
  currentFilters: Record<string, string>;
  currentColumns?: string[];
  onApply: (view: SavedViewDto) => void;
}) {
  const { user } = useAuth();
  const { data: views } = useSavedViews(scope);
  const save = useSaveView();
  const remove = useDeleteView();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [selected, setSelected] = useState('');
  const companies =
    user?.memberships.filter(
      (m, i, a) =>
        m.companyActive !== false && a.findIndex((x) => x.companyId === m.companyId) === i,
    ) ?? [];
  const [companyId, setCompanyId] = useState(companies[0]?.companyId ?? '');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const v = await save.mutateAsync({
        name,
        scope,
        filters: currentFilters,
        columns: currentColumns ?? null,
        isShared: shared,
        companyId: shared ? companyId : null,
      });
      toast.success('Vista guardada');
      setSelected(v.id);
      setOpen(false);
      setName('');
    } catch (err) {
      toast.error('No se pudo guardar la vista', (err as Error).message);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Select
        value={selected}
        onChange={(e) => {
          setSelected(e.target.value);
          const v = views?.find((x) => x.id === e.target.value);
          if (v) onApply(v);
        }}
        aria-label="Vista guardada"
        className="h-9 w-48"
      >
        <option value="">Vistas guardadas…</option>
        {views?.map((v) => (
          <option key={v.id} value={v.id}>
            {v.isShared ? '👥 ' : ''}
            {v.name}
          </option>
        ))}
      </Select>
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-9 w-9"
        onClick={() => setOpen(true)}
        aria-label="Guardar vista actual"
        title="Guardar vista actual"
      >
        <Bookmark className="h-4 w-4" />
      </Button>
      {selected && views?.find((v) => v.id === selected)?.isOwner && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label="Borrar vista"
          onClick={() => {
            if (!confirm('¿Borrar esta vista guardada?')) return;
            void remove.mutateAsync(selected).then(() => setSelected(''));
          }}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Guardar vista</DialogTitle>
            </DialogHeader>
            <div className="space-y-1">
              <Label htmlFor="view-name">Nombre</Label>
              <Input
                id="view-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                autoFocus
                placeholder="Ej. Vencidos META Castilla"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={shared}
                onChange={(e) => setShared(e.target.checked)}
                className="h-4 w-4"
              />{' '}
              Compartir con la empresa
            </label>
            {shared && companies.length > 1 && (
              <Select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                aria-label="Empresa"
              >
                {companies.map((c) => (
                  <option key={c.companyId} value={c.companyId}>
                    {c.companyName}
                  </option>
                ))}
              </Select>
            )}
            <p className="text-muted-foreground text-xs">
              Se guardan los filtros actuales{currentColumns ? ' y las columnas visibles' : ''}.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={save.isPending}>
                Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
